import { type ReactNode, useMemo } from 'react';
import type { Lens } from '@hookform/lenses';
import { z } from 'zod/v4';
import { Grid } from '@interxfi/design-system/components/alignment/grid';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import type { ResponsiveValue } from '@interxfi/design-system/util/responsive';
import { type I18nNamespaces, useTranslation } from '@interxfi/translation';
import { IsoV1Country, type IxcRegistrationV1AddressUsAddress, IxcRegistrationV1UsState } from '@interxfi/types/generated/api';
import { AppSelectInputWithLens } from '../../../../components/form/app-select-input';
import { AppTextInputWithLens } from '../../../../components/form/app-text-input';
import { buildStateOptions } from '../../../../util/country';
import { addDefinedValues } from '../../../../util/has-value';

export const usAddressFormSchema = z.object({
  type: z.literal(IsoV1Country.Us),
  value: z.object({
    street1: z.string().nonempty('Street Address is required'),
    street2: z.string(),
    city: z.string().nonempty('City is required'),
    state: z.enum(IxcRegistrationV1UsState, { error: 'State is required' }),
    postalCode: z.string().regex(/^[0-9]{5}(-[0-9]{4})?$/, { error: 'Must be a 5-digit ZIP or 9-digit ZIP+4 code' }),
  }),
});

export type USAddressFormValues = z.infer<typeof usAddressFormSchema>;

export const defaultUSAddressFormValues: USAddressFormValues = {
  type: IsoV1Country.Us,
  value: {
    street1: '',
    street2: '',
    city: '',
    state: '' as IxcRegistrationV1UsState,
    postalCode: '',
  },
};

export function formValuesToUSAddress(input: USAddressFormValues): IxcRegistrationV1AddressUsAddress {
  const { value } = input;

  return addDefinedValues<IxcRegistrationV1AddressUsAddress>(
    {
      street1: value.street1,
      city: value.city,
      state: value.state,
      postalCode: value.postalCode,
    },
    {
      street2: value.street2,
    },
  );
}

export function getInitialFormValuesForUSAddress(address: IxcRegistrationV1AddressUsAddress | undefined): USAddressFormValues | undefined {
  if (!address) {
    return undefined;
  }

  return {
    ...defaultUSAddressFormValues,
    value: {
      ...defaultUSAddressFormValues.value,
      ...address,
    },
  };
}

export type USAddressFormLabels = Partial<Record<keyof USAddressFormValues['value'], string>>;

export interface USAddressFormLayout {
  cityStateZipColumns: ResponsiveValue<number>;
}

export const DEFAULT_US_ADDRESS_FORM_LAYOUT = {
  cityStateZipColumns: {
    sm: 1,
    md: 3,
  },
} as const satisfies USAddressFormLayout;

export const STACKED_US_ADDRESS_FORM_LAYOUT = {
  cityStateZipColumns: {
    sm: 1,
  },
} as const satisfies USAddressFormLayout;

interface USAddressFormProps {
  countryInput?: ReactNode;
  labels?: USAddressFormLabels;
  layout?: USAddressFormLayout;
  lens: Lens<USAddressFormValues>;
  tooltip?: string;
}

const i18nNamespaces: I18nNamespaces[] = ['ixcRegistrationV1'] as const;

export function USAddressForm({ countryInput, labels, layout = DEFAULT_US_ADDRESS_FORM_LAYOUT, lens, tooltip }: USAddressFormProps) {
  const { t } = useTranslation(i18nNamespaces);
  const stateOptions = useMemo(() => buildStateOptions(t, IsoV1Country.Us), [t]);

  return (
    <VerticalStack gap={4}>
      <AppTextInputWithLens info={tooltip} infoWidth={220} label={labels?.street1 || 'Street Address'} lens={lens.focus('value.street1')} required />
      <AppTextInputWithLens label={labels?.street2 || 'Suite / Apt. #'} lens={lens.focus('value.street2')} />

      {countryInput}

      <Grid autoColumns={layout.cityStateZipColumns} gap={4}>
        <AppTextInputWithLens label={labels?.city || 'City'} lens={lens.focus('value.city')} required />
        <AppSelectInputWithLens label={labels?.state || 'State'} lens={lens.focus('value.state')} options={stateOptions} required />
        <AppTextInputWithLens label={labels?.postalCode || 'ZIP Code'} lens={lens.focus('value.postalCode')} required />
      </Grid>
    </VerticalStack>
  );
}
